import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import QuickActionButton from '../../components/ui/QuickActionButton';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import CameraViewfinder from './components/CameraViewfinder';
import OfflineIndicator from './components/OfflineIndicator';
import QuickActions from './components/QuickActions';
import { fahrtenService } from '../../services/fahrtenService';
import { useAuth } from '../../contexts/AuthContext';

const EndeZeitScanner = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [isScanning, setIsScanning] = useState(true); // Start scanning by default
  const [isLoading, setIsLoading] = useState(false);
  const [isOffline, setIsOffline] = useState(!navigator.onLine);
  const [pendingSyncs, setPendingSyncs] = useState(0);
  const [activeTab, setActiveTab] = useState('camera');

  // Error state for API issues
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);

  useEffect(() => {
    // Setup online/offline listeners
    const handleOnline = () => {
      setIsOffline(false);
      syncPendingData();
    };
    const handleOffline = () => setIsOffline(true);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const syncPendingData = () => {
    // This function is no longer needed since we're using the database
    // Keeping it for potential future offline functionality
    setPendingSyncs(0);
  };

  const handleVinDetected = async (vin, capturedImage = null) => {
    setIsLoading(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      if (!user?.id) {
        throw new Error('Benutzer nicht angemeldet');
      }

      // Find all fahrten with matching VIN
      const { data: fahrten, error: fetchError } = await fahrtenService.getFahrten(user.id);
      
      if (fetchError) {
        throw new Error(`Fehler beim Laden der Fahrten: ${fetchError.message}`);
      }

      // Find all fahrten with matching VIN
      const matchingFahrten = fahrten?.filter(fahrt => fahrt.vin === vin) || [];
      
      if (matchingFahrten.length === 0) {
        throw new Error(`Keine Fahrt mit VIN ${vin} gefunden`);
      }

      // Filter for fahrten without end time
      const fahrtenWithoutEndTime = matchingFahrten.filter(fahrt => 
        !fahrt.fahrt_uhrzeit_ende || fahrt.fahrt_uhrzeit_ende === ''
      );

      if (fahrtenWithoutEndTime.length === 0) {
        throw new Error(`Alle Fahrten mit VIN ${vin} haben bereits eine Ende-Zeit gesetzt`);
      }

      let fahrtToUpdate;

      if (fahrtenWithoutEndTime.length === 1) {
        // Only one fahrt without end time, update it automatically
        fahrtToUpdate = fahrtenWithoutEndTime[0];
      } else {
        // Multiple fahrten without end time, let user choose
        const fahrtOptions = fahrtenWithoutEndTime.map((fahrt, index) => {
          const date = new Date(fahrt.fahrt_datum).toLocaleDateString('de-DE');
          const startTime = fahrt.fahrt_uhrzeit_beginn || 'Nicht gesetzt';
          const vehicle = `${fahrt.vehicle_make || 'Unbekannt'} ${fahrt.vehicle_model || 'Unbekannt'}`;
          return `${index + 1}. ${vehicle} - ${date} ${startTime}`;
        }).join('\n');

        const choice = prompt(
          `Mehrere Fahrten mit VIN ${vin} gefunden, die noch keine Ende-Zeit haben:\n\n${fahrtOptions}\n\nBitte geben Sie die Nummer der Fahrt ein, die Sie aktualisieren möchten (1-${fahrtenWithoutEndTime.length}):`
        );

        if (!choice || isNaN(choice) || choice < 1 || choice > fahrtenWithoutEndTime.length) {
          throw new Error('Ungültige Auswahl oder abgebrochen');
        }

        fahrtToUpdate = fahrtenWithoutEndTime[parseInt(choice) - 1];
      }

      // Update the selected fahrt with current time as end time
      const currentTime = new Date().toTimeString().slice(0, 5);
      const { error: updateError } = await fahrtenService.updateFahrt(
        fahrtToUpdate.id, 
        { fahrt_uhrzeit_ende: currentTime }, 
        user.id
      );

      if (updateError) {
        throw new Error(`Fehler beim Aktualisieren der Ende-Zeit: ${updateError.message}`);
      }

      setSuccessMessage(`Ende-Zeit für ${fahrtToUpdate.vehicle_make} ${fahrtToUpdate.vehicle_model} (${vin}) auf ${currentTime} gesetzt!`);
      
    } catch (error) {
      console.error('Ende-Zeit Scanner error:', error);
      setError(error.message);
    } finally {
      setIsLoading(false);
      setIsScanning(false);
    }
  };

  const handleNewScan = () => {
    setError(null);
    setSuccessMessage(null);
    setActiveTab('camera');
    setIsScanning(true);
  };

  const tabs = [
    { id: 'camera', label: 'Kamera-Scan', icon: 'Camera' },
    { id: 'history', label: 'Verlauf', icon: 'History' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Header userRole="admin" />
             {/* Hide sidebar on mobile */}
       <div className="hidden lg:block">
         <Sidebar 
           isCollapsed={isSidebarCollapsed}
           onToggleCollapse={() => setIsSidebarCollapsed(!isSidebarCollapsed)}
           userRole="admin"
         />
       </div>
      <OfflineIndicator isOffline={isOffline} pendingSyncs={pendingSyncs} />
      <main className={`pt-16 pb-20 lg:pb-8 transition-all duration-200 ${
        isSidebarCollapsed ? 'lg:ml-16' : 'lg:ml-64'
      }`}>
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <Breadcrumb />
          
          {/* Page Header */}
          <div className="mb-6">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground mb-2">Ende-Zeit Scanner</h1>
                <p className="text-muted-foreground">
                  Scannen Sie VIN-Codes um die Ende-Zeit von Fahrten zu setzen
                </p>
              </div>
              
              <div className="hidden lg:flex items-center space-x-3">
                <Button
                  variant="outline"
                  onClick={() => navigate('/order-management-dashboard')}
                  iconName="ClipboardList"
                  iconPosition="left"
                >
                  Fahrten
                </Button>
                <Button
                  variant="default"
                  onClick={handleNewScan}
                  iconName="Clock"
                  iconPosition="left"
                >
                  Neuer Scan
                </Button>
              </div>
            </div>
          </div>

          {/* Tab Navigation */}
          <div className="mb-6">
            <div className="border-b border-border">
              <nav className="flex space-x-8 overflow-x-auto">
                {tabs?.map((tab) => (
                  <button
                    key={tab?.id}
                    onClick={() => setActiveTab(tab?.id)}
                    className={`flex items-center space-x-2 py-4 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                      activeTab === tab?.id
                        ? 'border-primary text-primary'
                        : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                    }`}
                  >
                    <Icon name={tab?.icon} size={16} />
                    <span>{tab?.label}</span>
                  </button>
                ))}
              </nav>
            </div>
          </div>

          {/* Success Message */}
          {successMessage && (
            <div className="bg-success/10 border border-success/20 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="CheckCircle" size={20} className="text-success mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-success">Erfolgreich</h4>
                  <p className="text-sm text-success/80 mt-1">{successMessage}</p>
                </div>
              </div>
            </div>
          )}

          {/* Error Display */}
          {error && (
            <div className="bg-error/10 border border-error/20 rounded-lg p-4 mb-6">
              <div className="flex items-center">
                <Icon name="AlertTriangle" size={20} className="text-error mr-3" />
                <div>
                  <h4 className="text-sm font-medium text-error">Fehler</h4>
                  <p className="text-sm text-error/80 mt-1">{error}</p>
                </div>
              </div>
            </div>
          )}

          {/* Tab Content */}
          <div className="space-y-6">
            {activeTab === 'camera' && (
              <div className="space-y-6">
                <CameraViewfinder
                  onVinDetected={handleVinDetected}
                  isScanning={isScanning}
                  onToggleCamera={() => setIsScanning(!isScanning)}
                />
                
                <div className="lg:hidden">
                  <QuickActions
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}

            {activeTab === 'history' && (
              <div className="space-y-6">
                <div className="bg-card rounded-lg border border-border p-6 text-center">
                  <Icon name="History" size={32} className="text-muted-foreground mx-auto mb-3" />
                  <p className="text-sm text-muted-foreground">Verlauf-Funktion für Ende-Zeit Scanner</p>
                  <p className="text-xs text-muted-foreground mt-2">Diese Funktion wird in einer zukünftigen Version verfügbar sein.</p>
                </div>
                
                <div className="lg:hidden">
                  <QuickActions
                    onNewScan={handleNewScan}
                  />
                </div>
              </div>
            )}

            {/* Desktop Quick Actions */}
            <div className="hidden lg:block">
              <QuickActions
                onNewScan={handleNewScan}
              />
            </div>
          </div>
        </div>
      </main>
      
      <QuickActionButton userRole="admin" />
    </div>
  );
};

export default EndeZeitScanner;